import { useState } from 'react'

export const useGeneticsCalculator = (entries) => {
  const [selectedMate, setSelectedMate] = useState('')
  const [breedingResults, setBreedingResults] = useState(null)

  // Genetics and breeding calculator functions
  const chickenGenes = {
    // E Locus - multiple alleles with dominance hierarchy
    'E': { 
      name: 'E Locus (Extension)', 
      alleles: {
        'E': { name: 'Extended Black', dominance: 6 },
        'ER': { name: 'Birchen', dominance: 5 },
        'eWh': { name: 'Wheaten', dominance: 4 },
        'eb': { name: 'Brown', dominance: 3 },
        'ey': { name: 'Recessive Wheaten', dominance: 2 },
        'e': { name: 'Red/Buff', dominance: 1 }
      },
      description: 'Controls base color pattern and melanin distribution'
    },
    'I': { name: 'Dominant White', dominant: 'I', recessive: 'i', description: 'Inhibits color expression' },
    'S': { name: 'Silver', dominant: 'S', recessive: 's', description: 'Silver vs gold base color' },
    'O': { name: 'Blue Egg', dominant: 'O', recessive: 'o', description: 'Blue/green egg shell color' },
    'R': { name: 'Rose Comb', dominant: 'R', recessive: 'r', description: 'Rose comb shape' },
    'P': { name: 'Pea Comb', dominant: 'P', recessive: 'p', description: 'Pea comb shape' },
    'F': { name: 'Frizzle', dominant: 'F', recessive: 'f', description: 'Curled feathers' },
    'Mf': { name: 'Muff/Beard', dominant: 'Mf', recessive: 'mf', description: 'Facial feathering' },
  }

  const getGeneticProfile = (bird) => {
    const genetics = bird?.genetics || {}
    // Convert new E locus format to combined format for compatibility
    if (genetics.E1 && genetics.E2) {
      genetics.E = [genetics.E1, genetics.E2]
    }
    return genetics
  }

  const calculateBreeding = (parent1, parent2) => {
    const p1Genetics = getGeneticProfile(parent1)
    const p2Genetics = getGeneticProfile(parent2)
    const results = []

    Object.keys(chickenGenes).forEach(gene => {
      if (gene === 'E') {
        // Special handling for E locus with multiple alleles
        const p1E = p1Genetics.E || ['?', '?']
        const p2E = p2Genetics.E || ['?', '?']
        
        if (p1E[0] !== '?' && p2E[0] !== '?') {
          const crosses = [
            [p1E[0], p2E[0]], [p1E[0], p2E[1]],
            [p1E[1], p2E[0]], [p1E[1], p2E[1]]
          ]
          
          const outcomes = {}
          crosses.forEach(cross => {
            // Sort by dominance for E locus
            const sorted = cross.sort((a, b) => {
              const aDom = chickenGenes.E.alleles[a]?.dominance || 0
              const bDom = chickenGenes.E.alleles[b]?.dominance || 0
              return bDom - aDom // Higher dominance first
            })
            const key = sorted.join('/')
            outcomes[key] = (outcomes[key] || 0) + 25
          })

          results.push({
            gene,
            name: chickenGenes[gene].name,
            parent1: p1E,
            parent2: p2E,
            outcomes: Object.entries(outcomes).map(([genotype, percentage]) => ({
              genotype: genotype.split('/'),
              percentage,
              phenotype: getELocusPhenotype(genotype.split('/'))
            }))
          })
        }
      } else {
        // Standard two-allele genes
        const gene1 = p1Genetics[gene] || ['?', '?']
        const gene2 = p2Genetics[gene] || ['?', '?']
        
        if (gene1[0] !== '?' && gene2[0] !== '?') {
          const crosses = [
            [gene1[0], gene2[0]], [gene1[0], gene2[1]],
            [gene1[1], gene2[0]], [gene1[1], gene2[1]]
          ]
          
          const outcomes = {}
          crosses.forEach(cross => {
            const sorted = cross.sort()
            const key = sorted.join('')
            outcomes[key] = (outcomes[key] || 0) + 25
          })

          results.push({
            gene,
            name: chickenGenes[gene].name,
            parent1: gene1,
            parent2: gene2,
            outcomes: Object.entries(outcomes).map(([genotype, percentage]) => ({
              genotype: genotype.split(''),
              percentage,
              phenotype: getPhenotype(gene, genotype.split(''))
            }))
          })
        }
      }
    })

    return results
  }

  const getELocusPhenotype = (genotype) => {
    const [allele1, allele2] = genotype
    const alleles = chickenGenes.E.alleles
    
    // Find the most dominant allele
    const dom1 = alleles[allele1]?.dominance || 0
    const dom2 = alleles[allele2]?.dominance || 0
    const dominantAllele = dom1 >= dom2 ? allele1 : allele2
    
    return alleles[dominantAllele]?.name || 'Unknown'
  }

  const getPhenotype = (gene, genotype) => {
    const geneInfo = chickenGenes[gene]
    const [allele1, allele2] = genotype
    
    if (allele1 === geneInfo.dominant || allele2 === geneInfo.dominant) {
      return `${geneInfo.name} (Dominant)`
    } else {
      return `Non-${geneInfo.name} (Recessive)`
    }
  }

  const handleBreedingCalculation = (entry) => {
    if (!selectedMate || !entry) return
    
    const mateEntry = entries.find(e => e.entryId === selectedMate)
    if (!mateEntry) return

    const results = calculateBreeding(entry, mateEntry)
    setBreedingResults({ mate: mateEntry, genetics: results })
  }

  return {
    // State
    selectedMate,
    setSelectedMate,
    breedingResults,
    setBreedingResults,
    
    // Constants
    chickenGenes,
    
    // Functions
    getGeneticProfile,
    getELocusPhenotype,
    getPhenotype,
    handleBreedingCalculation
  }
}